import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { Card, CardBody, CardFooter, CardHeader } from 'reactstrap';
import {
	getEstablishment,
	getFeedingBargesList,
} from '../../../../../slices/thunks';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const EstablishmentInfo = ({ id, height = 'auto' }) => {
	const dispatch = useDispatch();
	const [filteredFeedingBarges, setFilteredFeedingBarges] = useState([]);

	const selectorEstablishments = createSelector(
		state => state.Establishments,
		establishment => ({
			data: establishment.itemEstablishmentDetail,
			isLoading: establishment.isLoading,
			isSuccess: establishment.isSuccess,
			isError: establishment.isError,
		})
	);

	const { data, isLoading, isSuccess, isError } = useSelector(
		selectorEstablishments
	);

	const selectorFeedingBarges = createSelector(
		state => state.FeedingBarges,
		data => ({
			feeding_barges_list: data.itemFeedingBargesList,
		})
	);

	const { feeding_barges_list } = useSelector(selectorFeedingBarges);

	// Fetch establishment
	useEffect(() => {
		dispatch(getEstablishment(id));
	}, [id]);

	// Fetch feeding barges list
	useEffect(() => {
		dispatch(getFeedingBargesList({}));
	}, []);

	useEffect(() => {
		if (data && data.productive_cycle && feeding_barges_list) {
			const feedingBargesIds =
				data.productive_cycle.feeding_barges_ids || [];
			const filteredBarges = feeding_barges_list
				.filter(barge => feedingBargesIds.includes(barge._id))
				.map(barge => barge.name);

			setFilteredFeedingBarges(filteredBarges);
		}
	}, [data, feeding_barges_list]);

	const dateIni = data?.productive_cycle?.date_ini;
	const dateEnd = data?.productive_cycle?.date_end;

	const formattedDateIni = dateIni
		? format(parseISO(dateIni), 'MMMyyyy', { locale: es }).toUpperCase()
		: '-';
	const formattedDateEnd = dateEnd
		? format(parseISO(dateEnd), 'MMMyyyy', { locale: es }).toUpperCase()
		: '-';

	return (
		<Card className='p-0 mb-2' style={{ height: height }}>
			<CardHeader className='p-2 align-items-center d-flex'>
				<h5 className='flex-grow-1 m-0'>
					{data?.name?.toUpperCase() ?? '-'}
				</h5>
			</CardHeader>
			<CardBody className='p-2'>
				<ul className='list-unstyled mb-0 vstack gap-1'>
					<li>
						<span className='text-muted'>Estado</span>
						<span className='fw-semibold float-end'>
							<span
								className='badge'
								style={{
									backgroundColor: data?.establishment_status?.bgcolor,
									color: data?.establishment_status?.txcolor,
								}}
							>
								{data?.establishment_status?.name?.toUpperCase() ?? '-'}
							</span>
						</span>
					</li>
					<li>
						<span className='text-muted'>Tipo</span>
						<span className='fw-semibold float-end'>
							{data?.establishment_type?.name?.toUpperCase() ?? '-'}
						</span>
					</li>
					<li>
						<span className='text-muted'>Código SIEP</span>
						<span className='fw-semibold float-end'>
							{data?.code_siep ?? '-'}
						</span>
					</li>
					<li>
						<span className='text-muted'>Tipo de Propiedad</span>
						<span className='fw-semibold float-end'>
							<span
								className='badge'
								style={{
									backgroundColor: data?.property_type?.bgcolor,
									color: data?.property_type?.txcolor,
								}}
							>
								{data?.property_type?.name?.toUpperCase() ?? '-'}
							</span>
						</span>
					</li>
					<li>
						<span className='text-muted'>Empresa</span>
						<span className='fw-semibold float-end'>
							{data?.company?.name?.toUpperCase() ?? '-'}
						</span>
					</li>
				</ul>
			</CardBody>
			{['SWE', 'LWE', 'FWE'].includes(data?.establishment_type?.code) &&
				data?.productive_cycle && (
					<CardFooter className='p-2'>
						<ul className='list-unstyled mb-0 vstack gap-1'>
							<li>
								<span className='text-muted'>Ciclo Productivo</span>
								<span className='fw-semibold float-end'>
									{data?.productive_cycle?.code?.toUpperCase() ?? '-'}
								</span>
							</li>
							<li>
								<span className='text-muted'>Fechas</span>
								<span className='fw-semibold float-end'>
									{formattedDateIni && formattedDateEnd
										? `${formattedDateIni} - ${formattedDateEnd}`
										: '-'}
								</span>
							</li>
							<li>
								<span className='text-muted'>Estado</span>
								<span className='fw-semibold float-end'>
									<span
										className='badge'
										style={{
											backgroundColor:
												data?.productive_cycle
													?.productive_cycle_status?.bgcolor,
											color: data?.productive_cycle
												?.productive_cycle_status?.txcolor,
										}}
									>
										{data?.productive_cycle?.productive_cycle_status?.name?.toUpperCase() ??
											'-'}
									</span>
								</span>
							</li>
							<li>
								<span className='text-muted'>Etapa</span>
								<span className='fw-semibold float-end'>
									<span
										className='badge'
										style={{
											backgroundColor:
												data?.productive_cycle_stage?.bgcolor,
											color: data?.productive_cycle_stage?.txcolor,
										}}
									>
										{data?.productive_cycle_stage?.name?.toUpperCase() ??
											'-'}
									</span>
								</span>
							</li>
							<li>
								<span className='text-muted'>Artefacto Naval</span>
								<span className='fw-semibold float-end text-right'>
									{filteredFeedingBarges.length > 1 ? (
										<ul className='list-unstyled text-end m-0 p-0'>
											{filteredFeedingBarges.map((name, index) => (
												<li key={index} className='text-end'>
													{name.toUpperCase()}
												</li>
											))}
										</ul>
									) : filteredFeedingBarges.length === 1 ? (
										filteredFeedingBarges[0].toUpperCase()
									) : (
										'-'
									)}
								</span>
							</li>
						</ul>
					</CardFooter>
				)}
		</Card>
	);
};

export default EstablishmentInfo;
